import { ThemeContextProvider } from "@components/providers/ThemeContextProvider";
import { Link } from "@components/shared/core/Link";
import { NetflixAdsLogo } from "@components/shared/NetflixAdsLogo";
import { useRef } from "react";
import { useTranslation } from "@services/i18n";
import { Namespaces } from "@services/i18n/ObelixProvider";
import { IconArrowUpRight } from "@hawkins/assets";
import { FooterMediaGrid } from "./FooterMediaGrid";
import { SimpleNewsletterSignupForm } from "./SimpleNewsletterSignupForm";
import { filterByMarket, footerConfig, type FooterTextKey } from "./footerConfig";
import { OBELIX_FOOTER_KEYS } from "@utils/obelixKeys";
import type { Market } from "@components/shared/navigation/navConfig";
import { useGlobalContext } from "@utils/GlobalContext";
import type { FragmentOf, ResultOf } from "gql.tada";
import { LinkFragment } from "@queries/fragments/LinkFragment";
import { RichEntryFragment } from "@queries/fragments/RichEntryFragment";

interface FooterProps {
  /** Overrides the market derived from the GlobalContext `region` (tests). */
  market?: Market;
  /**
   * The Contentful rich entry for the newsletter sign-up info paragraph
   * (A2 — rich text stays in the CMS), passed through to the signup form.
   */
  newsletterSignUpInfoRichText?: FragmentOf<typeof RichEntryFragment> | null;
}

export const Footer = ({
  market: marketProp,
  newsletterSignUpInfoRichText = null,
}: FooterProps = {}) => {
  const { region, locale, activeRegion } = useGlobalContext();
  const market =
    marketProp ?? ((activeRegion as Market) ?? (region as Market));
  const { t } = useTranslation(Namespaces.footer);

  // The nav & footer have awkward overlap issues, so this ensures
  // that when the footer is in view, the header is hidden
  const footerRef = useRef<HTMLElement>(null);

  const footer = filterByMarket(footerConfig, market, locale);

  /** Resolves a footer display string via the join-key registry (D2). */
  const tFooter = (key: string) => {
    const entry = OBELIX_FOOTER_KEYS[key as FooterTextKey];
    return t(entry.obelixKey, entry.label);
  };

  /** Maps a config link node onto the `<Link>` component's link object. */
  const toLink = (item: {
    href: string;
    external: boolean;
    textKey: string;
  }): ResultOf<typeof LinkFragment> =>
    ({
      text: tFooter(item.textKey),
      isExternal: item.external,
      url: item.external ? item.href : null,
      slug: item.external ? null : item.href,
      __typename: "Link",
    }) as ResultOf<typeof LinkFragment>;

  return (
    <ThemeContextProvider
      theme={{
        palette: "red",
        foreground: "neutral-light",
        background: "true",
      }}
      className="text-palette-current-foreground"
    >
      <footer
        ref={footerRef}
        className="bg-palette-current-background flex flex-col gap-40 dsk:gap-52 container-base relative z-10"
      >
        <div className="dsk:pt-24 pt-20 dsk:pb-12 flex flex-col justify-between gap-16 dsk:gap-[344px]">
          <div className="flex flex-col dsk:flex-row justify-between">
            <div>
              <NetflixAdsLogo
                className="w-28 mb-7"
                secondarySectionProps={{
                  className: "text-neutral-light/60",
                }}
              />
              <SimpleNewsletterSignupForm
                footer={{
                  newsletterFormLabel: footer.newsletter.formLabelKey,
                  newsletterEmailFieldLabel: footer.newsletter.emailFieldLabelKey,
                  newsletterCta: footer.newsletter.ctaKey,
                  submissionApproved: footer.newsletter.submissionApprovedKey,
                  validEmailNote: footer.newsletter.validEmailNoteKey,
                  newsletterSignUpInfoRichText,
                }}
              />
            </div>
            <ul className="flex gap-y-12 gap-x-2 dsk:gap-4 flex-wrap">
              {footer.columns.map((linkGroup, index) => (
                <li key={index} className="flex-1">
                  <p className="text-body-small pb-5 dsk:pb-7">
                    {tFooter(linkGroup.titleKey)}
                  </p>
                  <ul className="flex flex-col gap-3">
                    {linkGroup.links.map((item, index) => {
                      const link = toLink(item);

                      return (
                        <li key={index}>
                          <Link
                            link={link}
                            className="opacity-70 text-body-small font-normal"
                          >
                            {link.text}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              ))}
            </ul>
          </div>
          <div className="flex flex-col-reverse gap-16 dsk:flex-row justify-between w-full">
            <p className="text-body-small">{tFooter(footer.singleton.copyrightKey)}</p>
            <ul className="flex gap-5 dsk:gap-10 flex-col dsk:flex-row">
              {footer.bottom.links.map((item, index) => {
                const link = toLink(item);
                return (
                  <li key={index}>
                    <Link
                      link={link}
                      className="text-body-regular dsk:text-body-small inline-flex gap-2"
                    >
                      {link.text}
                      {link.isExternal && <IconArrowUpRight size="small" />}
                    </Link>
                  </li>
                );
              })}
              <li>
                <button
                  className="appearance-none text-body-regular dsk:text-body-small"
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  onClick={() => (window as any).Optanon.ToggleInfoDisplay()}
                >
                  {tFooter(footer.singleton.cookiesKey)}
                </button>
              </li>
            </ul>
          </div>
        </div>
        <div className="flex justify-center items-center w-full">
          <p className="text-display uppercase text-center max-w-7xl">
            {tFooter(footer.singleton.easterEggKey)}
          </p>
        </div>
        <div className="flex flex-col gap-20 dsk:gap-52">
          <FooterMediaGrid mediaItems={[...footer.media]} />
          <NetflixAdsLogo
            className="mb-10 dsk:mb-7 w-full"
            secondarySectionProps={{
              className: "text-neutral-light/60",
            }}
          />
        </div>
      </footer>
    </ThemeContextProvider>
  );
};
